// SPDX-FileCopyrightText: 2026 Z-Meet contributors
// SPDX-License-Identifier: Apache-2.0

import { useEffect, useRef, useState } from 'react'
import {
  Microphone,
  MicrophoneSlash,
  VideoCamera,
  VideoCameraSlash,
  PhoneX,
  Record,
  StopCircle,
  LockKey,
  LockKeyOpen,
  Check,
  UserMinus,
  ChatCircle,
  SmileyWink,
  ImageSquare,
  Prohibit,
  HandWaving,
  PaperPlaneRight,
  X,
  UploadSimple,
  Monitor,
  SmileySticker,
  SquaresFour,
  UserPlus,
  Copy,
} from '@phosphor-icons/react'
import { useI18n } from '../i18n'
import * as api from '../api'
import { outranks } from '../roles'
import { useRoomData } from '../roomdata'
import {
  useLocalRecorder,
  elapsed,
  supported as recSupported,
} from '../recorder'
import { CATALOG } from '../emoji'
import { Emoji, RichText } from '../components/Emoji'
import {
  applyBackground,
  supported as bgSupported,
  addImage,
  listImages,
  removeImage,
  MAX_IMAGES,
  type BackgroundMode,
  type StoredBackground,
} from '../background'
import type { Tile, useRoom } from '../livekit'

export type RoomCtx = {
  sessionID: string
  participantID: string
  /**
   * Principal ref nhận lúc vào phòng. Đây là thứ CHỨNG MINH danh tính cho các
   * lời gọi không đi qua access token — khách không có token nào khác.
   */
  principal: string
  workspace: string | null
  role: string
  /** Tên cuộc họp. Đi vào tên file của bản ghi cục bộ — xem recorder.ts. */
  title: string
}

type Props = {
  room: ReturnType<typeof useRoom>
  ctx: RoomCtx
  myName: string
  waiting: api.Waiting[]
  recording: boolean
  /** Phòng đã khoá chưa — nút Khoá/Mở khoá đọc nó để biết mình đang là nút gì. */
  locked: boolean
  /** Người này vừa tự khoá hoặc mở khoá. Xem chú thích ở nút Khoá. */
  onLockedChange: (locked: boolean) => void
  consentNeeded: boolean
  /**
   * Chế độ ghi hình của triển khai — ĐÚNG MỘT trong hai tồn tại cùng lúc.
   *
   * Hai nút ghi hình trong màn hình này phục vụ hai chế độ khác nhau và không
   * bao giờ được hiện cùng nhau: chúng khác nhau ở ai trả chi phí máy và ai giữ
   * file, và không có nhãn nào đủ ngắn để phân biệt hai điều đó cho người đang
   * vội bấm. Người vận hành đã chọn giúp họ (ZMEET_RECORDING_MODE).
   */
  recordingMode: api.RecordingMode
  /**
   * Tăng mỗi lần một bản ghi phía server tải xong lên kho object.
   *
   * Một con SỐ chứ không phải id: sự kiện là GỢI Ý, không phải dữ liệu
   * (ADR-0006 §5). Nó chỉ nói "đi hỏi lại API", còn link thật thì hỏi server.
   */
  recordingReady: number
  /** Lời mời bật lại mic/camera do host gửi; null khi không có. */
  unmuteAsk: 'audio' | 'video' | null
  onDismissUnmuteAsk: () => void
  onRefreshWaiting: () => void
  onLeave: () => void
}

const HOST_ROLES = ['host', 'cohost']

/**
 * Vai trò nào được đánh dấu ngay cạnh tên trên ô video.
 *
 * Chỉ những vai trò ĐIỀU HÀNH được. Một cuộc gọi mở từ Z-Mesh có thể có vài
 * host cùng lúc và phần còn lại là presenter/attendee; đánh dấu cả bốn mức là
 * biến một dấu hiệu thành một hàng chữ mà không ai đọc.
 */
const ROLE_BADGES: Record<string, string | undefined> = {
  host: 'ui.room.role_host',
  cohost: 'ui.room.role_cohost',
}

/**
 * Ai thấy nút "Thông tin cuộc họp".
 *
 * Presenter chứ không chỉ host, và đó là quyết định bị ép bởi thực tế: vé tham
 * dự bị chặn trần ở presenter (ADR-0007 bất biến 7), nên một cuộc gọi mở từ hệ
 * thống ngoài KHÔNG CÓ AI là host. Gate ở host nghĩa là nút này không bao giờ
 * hiện ra ở đúng nơi nó sinh ra để phục vụ.
 *
 * Attendee thì không: ở một cuộc gọi hỗ trợ, người gọi tới không nên kéo được
 * người lạ vào phòng. Server cưỡng chế cùng luật này (meeting.MinRoleToInvite);
 * danh sách ở đây chỉ để không vẽ ra một cái nút dẫn tới 403.
 */
const INVITE_ROLES = ['host', 'cohost', 'presenter']

export function Room({
  room,
  ctx,
  myName,
  waiting,
  recording,
  locked,
  onLockedChange,
  consentNeeded,
  recordingMode,
  recordingReady,
  unmuteAsk,
  onDismissUnmuteAsk,
  onRefreshWaiting,
  onLeave,
}: Props) {
  const { t } = useI18n()
  const isHost = HOST_ROLES.includes(ctx.role)
  const ws = ctx.workspace

  /**
   * Bằng chứng danh tính cho MỌI thao tác điều hành.
   *
   * Thành viên workspace dùng access token + workspace; khách dùng cặp
   * (principal_ref, participant_id) nhận lúc vào phòng. Phải có cả hai đường vì
   * một cuộc gọi mở từ hệ thống ngoài không có thành viên workspace nào tham
   * dự — xem api.RoomActor và internal/httpapi/middleware.go.
   *
   * ⚠️ Vai trò KHÔNG suy ra từ đây. `isHost` đọc từ ctx.role, thứ do server đặt
   * lúc vào phòng; đây chỉ là cách nói "tôi là ai", không phải "tôi được làm gì".
   */
  const actor: api.RoomActor = ws
    ? { workspace: ws }
    : { as: ctx.principal, actor: ctx.participantID }

  const canInvite = INVITE_ROLES.includes(ctx.role)

  // ⚠️ Ghi hình CỤC BỘ, không phải nút "Ghi hình" của host ở bảng điều hành.
  // Cái này không gọi API nào và bản ghi nằm lại trên máy người bấm; ai trong
  // phòng cũng dùng được, nên nó nằm ở thanh điều khiển chung chứ không sau
  // hàng rào vai trò. Xem recorder.ts.
  //
  // Hook vẫn chạy ở CẢ HAI chế độ — gọi hook có điều kiện là phá luật của React
  // — nhưng ở chế độ server thì không có nút nào bấm được nó.
  const rec = useLocalRecorder(room, ctx.title || myName)
  const data = useRoomData(room.room, myName, rec.recording)
  const serverMode = recordingMode === 'server'
  const [shareLink, setShareLink] = useState<api.ShareLink | null>(null)
  const [shareNote, setShareNote] = useState('')

  // Bản ghi phía server vừa lên xong kho object thì đi HỎI LẠI API để lấy link.
  //
  // `recordingReady` chỉ là một con số tăng dần: sự kiện quyết định KHI NÀO gọi,
  // không mang dữ liệu nào để tin (ADR-0006 §5). Nhờ vậy một sự kiện bị mất chỉ
  // làm link hiện muộn — host bấm lại panel là có — chứ không dựng ra một link
  // sai từ payload.
  useEffect(() => {
    if (recordingReady === 0 || !serverMode || !isHost) return
    void api.recordingShareLink(ctx.sessionID, actor).then((r) => {
      if (r.ok) setShareLink(r.data)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordingReady, serverMode, isHost, ctx.sessionID])
  const [recError, setRecError] = useState('')
  const [panel, setPanel] = useState<'none' | 'chat' | 'people' | 'invite'>('none')
  const [draft, setDraft] = useState('')
  const [pickReaction, setPickReaction] = useState(false)
  const [pickChatEmoji, setPickChatEmoji] = useState(false)
  const [screenError, setScreenError] = useState('')
  const [showPeople, setShowPeople] = useState(false)
  const [bg, setBg] = useState<BackgroundMode>({ kind: 'none' })
  const [bgError, setBgError] = useState('')
  const [bgPanel, setBgPanel] = useState(false)
  const [images, setImages] = useState<StoredBackground[]>(() => listImages())
  /**
   * Nút "Kết thúc cho tất cả" đang ở bước hỏi lại.
   *
   * Xác nhận NGAY TRONG nút, không phải `confirm()`: Z-Meet chạy trong iframe
   * của hệ thống khác (ADR-0007 §7), và hộp thoại của trình duyệt bị chặn ở đó
   * trừ khi trang cha khai `allow-modals` — tức là một xác nhận có lúc không
   * bao giờ hiện ra, và cú bấm đầu tiên đã đá cả phòng ra ngoài.
   *
   * ⚠️ Bước hỏi lại KHÔNG được gỡ bằng `onBlur`, và bản trước đã làm thế.
   *
   * Ý định thì đúng — một cái nút nguy hiểm không nên nằm chờ mãi ở trạng thái
   * "bấm lần nữa là xong". Nhưng focus là thứ SAI để treo nó vào: cú bấm thứ
   * hai chỉ tới nơi nếu không có gì chen vào giữa hai lần bấm, mà bảng điều
   * khiển này thì vẽ lại liên tục theo người vào/ra/nói. Một cú chạm lạc ra
   * ngoài nút cũng gỡ vũ trang, im lặng, và lần bấm sau chỉ vũ trang lại — nút
   * không bao giờ chạy, và không có gì hiện ra để nói vì sao.
   *
   * Nó hỏng đúng như vậy trên production: tám phiên liên tiếp, không phiên nào
   * `end_reason = host_ended`, trong khi những nút khác cùng bảng (khoá, tắt
   * mic tất cả) đi qua CÙNG một cửa xác thực và chạy bình thường.
   *
   * Thay bằng ĐỒNG HỒ: vũ trang vài giây rồi tự gỡ. Cùng tính chất an toàn —
   * nút không nằm chờ mãi — mà không phụ thuộc vào một thứ người dùng không
   * điều khiển được và không nhìn thấy.
   */
  const [endConfirm, setEndConfirm] = useState(false)
  /** Lời gọi kết thúc vừa hỏng. Rỗng nghĩa là chưa có gì để nói. */
  const [endError, setEndError] = useState('')
  const fileRef = useRef<HTMLInputElement>(null)

  const applyBg = async (mode: BackgroundMode) => {
    if (!room.localVideo) {
      // Không có track camera thì không có gì để gắn bộ xử lý vào. Nói ra thay
      // vì im lặng không làm gì: người dùng vừa chọn một ảnh và cần biết vì sao
      // màn hình không đổi.
      setBgError(t('ui.room.background_needs_camera'))
      return
    }
    setBgError('')
    try {
      await applyBackground(room.localVideo, mode)
      setBg(mode)
    } catch (e) {
      // Nói thẳng. Im lặng thất bại ở đây nghĩa là người dùng tưởng nền đã bật
      // trong khi phòng của họ vẫn hiện ra với tất cả mọi người.
      setBgError(t('ui.room.background_failed', { error: (e as Error).message }))
      setBg({ kind: 'none' })
    } finally {
      // Bắt buộc, kể cả khi lỗi. Bật/tắt bộ xử lý thay track bên trong
      // LocalVideoTrack nhưng không phát sự kiện, nên ô của chính mình phải
      // được dựng lại bằng tay — nếu không, mọi người thấy nền mới ngay còn
      // mình thì vẫn nhìn phòng khách của mình. Ở nhánh lỗi thì càng phải gọi:
      // bộ xử lý có thể đã đổi track trước lúc ném lỗi.
      room.refresh()
    }
  }

  // Tắt rồi bật camera tạo một track MỚI, và bộ xử lý gắn vào track cũ đi theo
  // nó. Không áp lại thì người dùng bật camera lên và phòng khách của họ hiện
  // ra — đúng thứ họ vừa bật nền để tránh.
  const lastTrack = useRef<unknown>(null)
  useEffect(() => {
    if (!room.localVideo || room.localVideo === lastTrack.current) return
    lastTrack.current = room.localVideo
    if (bg.kind !== 'none')
      void applyBackground(room.localVideo, bg)
        .then(() => room.refresh())
        .catch(() => {})
  }, [room.localVideo, bg])

  // Tự gỡ vũ trang nút "Kết thúc cho tất cả" sau vài giây.
  //
  // Đây là thứ THAY CHO `onBlur` — xem ghi chú ở `endConfirm`. Cùng mục đích
  // (một nút nguy hiểm không nằm chờ mãi), nhưng treo vào thời gian thay vì
  // vào focus: người dùng thấy được đồng hồ đếm qua chữ trên nút, còn focus
  // thì họ không điều khiển được và không nhìn thấy.
  //
  // Năm giây đủ rộng cho một cú bấm thứ hai có chủ ý — kể cả khi tay rời chuột
  // để đọc dòng cảnh báo vừa hiện ra — và đủ hẹp để không ai quay lại sau bữa
  // trưa mà bấm trúng một cái nút vẫn đang vũ trang.
  useEffect(() => {
    if (!endConfirm) return
    const id = setTimeout(() => setEndConfirm(false), 5000)
    return () => clearTimeout(id)
  }, [endConfirm])

  // Một chỗ dựng ô duy nhất, dùng cho cả lưới chính lẫn cột thu nhỏ. Nhân đôi
  // ba mươi dòng điều hành ra hai chỗ là cách chắc chắn để một ngày nào đó
  // "tắt micro" chạy ở lưới mà không chạy ở cột.
  const renderTile = (tile: Tile) => (
    <VideoTile
      key={tile.id}
      tile={tile}
      // BA điều kiện, và điều kiện thứ ba là thứ vắng mặt suốt một thời gian
      // dài: `isHost` nói "tôi được điều hành", nhưng không nói "tôi được
      // điều hành NGƯỜI NÀY". Server đòi xếp trên hẳn (ADR-0002 bất biến 10),
      // nên hai người cùng vai — hai host trong một cuộc gọi mở từ Z-Mesh là
      // ca thường gặp — thấy đủ menu trên ô của nhau và mọi nút trả 403.
      //
      // `tile.role` đọc từ metadata của LiveKit, tức là từ vai trò THẬT mà
      // server đặt lúc vào phòng; nó là cùng nguồn với dấu vai trò ở dưới.
      canModerate={isHost && !tile.isLocal && outranks(ctx.role, tile.role)}
      // Tra theo identity chứ không theo tile.id: người đang chia sẻ màn hình
      // có hai ô, và dấu ghi hình phải nằm trên cả hai. Ở chế độ chỉ-màn-hình,
      // ô mặt người bị ẩn — đó chính là lúc dấu trên ô màn hình là thứ duy nhất
      // còn nhìn thấy được.
      recording={data.recorders.has(tile.identity)}
      reactions={data.reactions.filter((r) => r.from === tile.name).map((r) => r.emoji)}
      onModerate={async (action) => {
        if (!tile.pid) return
        switch (action) {
          case 'mute-audio':
            await api.muteParticipant(ctx.sessionID, tile.pid, actor, 'audio')
            break
          case 'mute-video':
            await api.muteParticipant(ctx.sessionID, tile.pid, actor, 'video')
            break
          case 'stop-screen':
            await api.muteParticipant(ctx.sessionID, tile.pid, actor, 'screen')
            break
          case 'ask-audio':
            await api.askUnmute(ctx.sessionID, tile.pid, actor, 'audio')
            break
          case 'ask-video':
            await api.askUnmute(ctx.sessionID, tile.pid, actor, 'video')
            break
          case 'block':
            await api.setPublish(ctx.sessionID, tile.pid, actor, false)
            break
          case 'allow':
            await api.setPublish(ctx.sessionID, tile.pid, actor, true)
            break
          case 'kick':
            // Mức nhẹ: họ vào lại được, nhưng rơi vào phòng chờ và host phải
            // bấm Duyệt lần nữa. Xem api.kick.
            await api.kick(ctx.sessionID, tile.pid, actor)
            break
          case 'ban':
            await api.kick(ctx.sessionID, tile.pid, actor, '', true)
            break
        }
      }}
    />
  )

  const people = room.tiles.filter((tile) => !tile.isScreen)
  const hasScreen = people.length !== room.tiles.length
  // Khi có chia sẻ, lưới chính CHỈ có màn hình; mọi người sang cột bên phải.
  const shown = hasScreen ? room.tiles.filter((tile) => tile.isScreen) : room.tiles

  // Mỗi lần MỘT phiên chia sẻ bắt đầu thì quay về chế độ chỉ màn hình, kể cả
  // khi lần trước người xem đã bật lưới lên. Chia sẻ mới là một sự kiện mới —
  // người trình bày vừa đưa lên thứ họ muốn mọi người nhìn.
  useEffect(() => {
    if (hasScreen) setShowPeople(false)
  }, [hasScreen])

  return (
    <>
      {recording && (
        <div className="banner">
          <Record size={16} weight="light" />
          <strong>{t('ui.recording.banner')}</strong>
          {consentNeeded && ws && (
            <>
              <span className="spacer" />
              <button className="sm" onClick={() => void api.ackRecording(ctx.sessionID, ws)}>
                {t('ui.recording.acknowledge')}
              </button>
            </>
          )}
        </div>
      )}

      {/* Ghi hình cục bộ của bất kỳ ai trong phòng, kể cả mình.

          Băng riêng chứ không gộp vào băng ghi hình phía trên: hai thứ này khác
          nhau ở đúng điều người ta cần biết. Bản ghi của host nằm ở workspace và
          có chính sách lưu trữ; bản ghi cục bộ nằm trên máy một cá nhân và không
          ai lấy lại được. Gộp chúng lại là nói với người dùng một câu sai. */}
      {data.recorders.size > 0 && (
        <div className="banner">
          <Record size={16} weight="light" className="rec" />
          <strong>
            {t('ui.localrec.banner', {
              names: [...data.recorders.values()].join(', '),
            })}
          </strong>
          {rec.recording && (
            <>
              <span className="spacer" />
              {/* Nói trước rằng file chỉ xuất hiện lúc dừng. Không nói thì
                  người dùng đi tìm nó trong thư mục Tải về giữa buổi họp, không
                  thấy, và kết luận là tính năng hỏng. */}
              <span className="meta">{t('ui.localrec.saved_on_stop')}</span>
              <span className="clock">{elapsed(rec.seconds)}</span>
              <button className="sm" onClick={() => rec.stop()}>
                <StopCircle size={15} weight="light" />
                {t('ui.localrec.stop')}
              </button>
            </>
          )}
        </div>
      )}

      {room.mediaLost && (
        // Mất media KHÔNG phải là rời cuộc họp (ADR-0001 §8): quyền điều hành
        // vẫn còn và bảng bên phải vẫn dùng được.
        <div className="banner">{t('ui.grid.media_lost')}</div>
      )}

      {unmuteAsk && (
        <div className="banner ask">
          <HandWaving size={16} weight="light" />
          {t(unmuteAsk === 'audio' ? 'ui.room.ask_unmute_audio' : 'ui.room.ask_unmute_video')}
          <span className="spacer" />
          {/* ⚠️ Host KHÔNG bật hộ được — đây là lời mời, và nút này là của
              chính chủ. Xem internal/meeting/moderate.go. */}
          <button
            className="sm"
            onClick={() => {
              void (unmuteAsk === 'audio' ? room.setMic(true) : room.setCam(true))
              onDismissUnmuteAsk()
            }}
          >
            {t('ui.room.ask_unmute_accept')}
          </button>
          <button className="sec sm" onClick={onDismissUnmuteAsk}>
            {t('ui.room.ask_unmute_decline')}
          </button>
        </div>
      )}

      <div className="stage">
        {/* Đang có người chia sẻ màn hình thì ô của mọi người rời khỏi sân khấu
            chính. Lý do không phải thẩm mỹ: lưới `auto-fit, minmax(240px)` chia
            đều chỗ cho mọi ô, nên một slide phải nằm cạnh sáu khuôn mặt và co
            lại đúng bằng chúng — chữ trên slide thành không đọc nổi, mà slide
            mới là thứ người ta đang nhìn.

            Nút bốn ô mở một CỘT BÊN PHẢI chứa thu nhỏ của mọi người, thay vì
            trả họ về lưới chính. Trả về lưới thì slide lại co lại đúng như cũ
            — tức là quay về đúng vấn đề vừa giải quyết. Cột bên lấy chỗ theo
            chiều ngang, chỗ mà một slide 16:9 không dùng tới trên màn hình rộng.

            Đây là ẩn, KHÔNG phải huỷ đăng ký track: `showPeople` chỉ lọc lúc
            dựng. Người xem mở cột lên là thấy ngay, không phải chờ thương
            lượng lại. Đổi lại là băng thông vẫn tốn cho video không hiện — chỗ
            này chờ selective subscription của LiveKit, xem web/README. */}
        {hasScreen && (
          <button
            className={'grid-toggle' + (showPeople ? ' on' : '')}
            aria-label={t(showPeople ? 'ui.room.hide_people' : 'ui.room.show_people')}
            aria-pressed={showPeople}
            aria-expanded={showPeople}
            onClick={() => setShowPeople((v) => !v)}
          >
            <SquaresFour size={20} weight="light" />
            <span>{people.length}</span>
          </button>
        )}
        <div className={'grid' + (hasScreen ? ' focus' : '')}>
          {room.tiles.length === 0 ? (
            <p className="empty">{t('ui.grid.empty_room')}</p>
          ) : (
            shown.map(renderTile)
          )}
        </div>

        {/* Cột thu nhỏ. Chỉ tồn tại khi ĐANG có người chia sẻ — không có chia
            sẻ thì lưới chính đã là chỗ đúng cho mọi người, và một cột thứ hai
            chỉ lấy mất chỗ của chính những khuôn mặt đó. */}
        {hasScreen && showPeople && (
          <aside className="thumbs">
            <div className="row">
              <h2 style={{ flex: 1 }}>{t('ui.room.people_heading', { count: String(people.length) })}</h2>
              <button
                className="sec sm"
                onClick={() => setShowPeople(false)}
                aria-label={t('ui.room.hide_people')}
              >
                <X size={14} weight="light" />
              </button>
            </div>
            {people.length === 0 ? (
              <p className="empty">{t('ui.grid.empty_room')}</p>
            ) : (
              people.map(renderTile)
            )}
          </aside>
        )}

        {panel === 'chat' && (
          <aside className="sidebar">
            <div className="row">
              <h2 style={{ flex: 1 }}>{t('ui.chat.heading')}</h2>
              <button className="sec sm" onClick={() => setPanel('none')} aria-label={t('ui.chat.close')}>
                <X size={14} weight="light" />
              </button>
            </div>
            {/* Nói thẳng ngay trong giao diện: chat không có lịch sử. Người dùng
                phải biết trước khi họ gõ một thứ quan trọng vào đó. */}
            <p className="meta">{t('ui.chat.ephemeral')}</p>

            <ul className="list chat">
              {data.messages.length === 0 ? (
                <li className="empty">{t('ui.chat.empty')}</li>
              ) : (
                data.messages.map((m) => (
                  <li key={m.id} className={m.mine ? 'mine' : ''}>
                    <div className="meta">{m.from}</div>
                    <div className="text">
                      <RichText text={m.text} />
                    </div>
                  </li>
                ))
              )}
            </ul>

            {pickChatEmoji && (
              <div className="emoji-picker">
                {CATALOG.map((e) => (
                  <button
                    key={e.name}
                    className="sec"
                    aria-label={e.name}
                    onClick={() => {
                      // Chèn shortcode chứ không chèn ảnh: ô nhập là văn bản
                      // thuần, và người dùng sửa/xoá được như mọi chữ khác.
                      setDraft((d) => (d ? d + ' ' : '') + `:${e.name}: `)
                      setPickChatEmoji(false)
                    }}
                  >
                    <Emoji name={e.name} size={26} />
                  </button>
                ))}
              </div>
            )}

            <form
              className="row"
              onSubmit={(e) => {
                e.preventDefault()
                const text = draft.trim()
                if (!text) return
                void data.sendChat(text)
                setDraft('')
              }}
            >
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={t('ui.chat.placeholder')}
                aria-label={t('ui.chat.placeholder')}
              />
              <button
                type="button"
                className="sec"
                aria-label={t('ui.chat.emoji')}
                onClick={() => setPickChatEmoji((v) => !v)}
              >
                <SmileySticker size={16} weight="light" />
              </button>
              <button type="submit" disabled={!draft.trim()} aria-label={t('ui.chat.send')}>
                <PaperPlaneRight size={16} weight="light" />
              </button>
            </form>
          </aside>
        )}

        {panel === 'invite' && canInvite && (
          <InvitePanel ctx={ctx} onClose={() => setPanel('none')} />
        )}

        {panel === 'people' && isHost && (
          <aside className="sidebar">
            <div className="row">
              <h2 style={{ flex: 1 }}>{t('ui.host.heading')}</h2>
              <button className="sec sm" onClick={() => setPanel('none')} aria-label={t('ui.chat.close')}>
                <X size={14} weight="light" />
              </button>
            </div>

            <div className="row" style={{ flexWrap: 'wrap', marginBottom: 12 }}>
              {/* Ghi hình PHÍA SERVER vẫn chỉ dành cho thành viên workspace, và
                  đó là ranh giới có lý chứ không phải chỗ chưa làm xong: nó tiêu
                  hạn mức của gói, ghi file vào kho của workspace, và cần một
                  người chịu trách nhiệm về bản ghi đó. Host của một cuộc gọi mở
                  từ hệ thống ngoài là một danh tính do hệ thống đó bảo lãnh —
                  đủ để điều hành cái phòng, không đủ để tiêu tiền của workspace.

                  Ẩn thay vì hiện rồi báo lỗi: một cái nút luôn trả 401 là cách
                  bắt người dùng tự phát hiện giới hạn hộ ta. Ghi hình về máy cá
                  nhân thì ai cũng bấm được, và nút đó nằm ở thanh dưới.

                  `serverMode` là hàng rào thứ hai, và nó đến từ người VẬN HÀNH
                  chứ không từ vai trò: ở một triển khai chạy ghi cục bộ thì nút
                  này không có gì để gọi. Server cưỡng chế cùng luật
                  (meeting.ErrServerRecordingOff) — chỗ này chỉ để không vẽ ra
                  một cái nút dẫn tới 409. */}
              {ws && serverMode && (
                <button
                  className="sec sm"
                  onClick={() =>
                    void (recording
                      ? api.stopRecording(ctx.sessionID, ws)
                      : api.startRecording(ctx.sessionID, ws))
                  }
                >
                  {recording ? <StopCircle size={16} weight="light" /> : <Record size={16} weight="light" />}
                  {t(recording ? 'ui.host.stop_record' : 'ui.host.record')}
                </button>
              )}
              {/* Khoá phòng, và mở lại. MỘT nút đổi nghĩa theo `locked`, không
                  phải hai nút cạnh nhau: đúng một trong hai thao tác có nghĩa ở
                  mỗi thời điểm, và hai nút thì nút vô nghĩa vẫn bấm được.

                  Cái nút này trước đây không có trạng thái nào — nó luôn hiện
                  chữ "Khoá phòng", nên host bấm, không thấy gì đổi, và bấm lại.
                  Lần bấm thứ hai trả 404 (xem store.SessionStore.Lock): một lỗi
                  cho thao tác đã thành công từ lần đầu. Server giờ nhận lần bấm
                  thứ hai là 204, còn chỗ này lo phần còn lại — không thấy gì
                  đổi mới là cái khiến người ta bấm lại.

                  Tự đặt trạng thái chứ không chỉ chờ sự kiện: `session.locked`
                  và `session.unlocked` chỉ phát ở lần CHUYỂN thật, nên một host
                  bấm Khoá ở phòng đã khoá sẵn không nhận được sự kiện nào.

                  Không hỏi lại trước khi mở khoá dù nó nới lỏng một hàng rào an
                  toàn: hoàn tác được ngay bằng chính cái nút đó, và một hộp xác
                  nhận trên một thao tác đảo được là thứ người ta học cách bấm
                  qua — rồi bấm qua luôn cái hộp của nút Kết thúc ngay bên dưới,
                  thứ KHÔNG hoàn tác được. */}
              <button
                className="sec sm"
                onClick={async () => {
                  const r = locked
                    ? await api.unlock(ctx.sessionID, actor)
                    : await api.lock(ctx.sessionID, actor)
                  if (r.ok) onLockedChange(!locked)
                }}
              >
                {locked ? (
                  <LockKeyOpen size={16} weight="light" />
                ) : (
                  <LockKey size={16} weight="light" />
                )}
                {t(locked ? 'ui.host.unlock' : 'ui.host.lock')}
              </button>
              <button className="sec sm" onClick={() => void api.muteAll(ctx.sessionID, actor)}>
                <MicrophoneSlash size={16} weight="light" />
                {t('ui.host.mute_all')}
              </button>
              {/* Kết thúc cho TẤT CẢ — khác hẳn nút đỏ ở thanh dưới, thứ chỉ
                  đưa người bấm ra ngoài. Nằm ở đây chứ không cạnh nút rời phòng
                  vì hai thao tác trông giống nhau ở khoảng cách một cú bấm
                  nhầm, và cái thứ hai không hoàn tác được.

                  Với cuộc gọi mở từ hệ thống ngoài, đây cũng là thứ báo cho hệ
                  thống đó biết cuộc họp đã tàn — xem api.endSession. */}
              <button
                className="sec sm danger"
                onClick={() => {
                  if (!endConfirm) {
                    setEndConfirm(true)
                    return
                  }
                  setEndConfirm(false)
                  setEndError('')
                  // BẮT lỗi, không `void`.
                  //
                  // Bản trước bỏ qua promise, và đó là lý do lỗi này sống được
                  // lâu như vậy: khi lời gọi hỏng, người bấm thấy y hệt lúc nó
                  // chạy — cái nút trở lại chữ "Kết thúc cho tất cả" và không
                  // có gì khác xảy ra. Họ kết luận đã kết thúc rồi, rời phòng,
                  // và cuộc họp sống tiếp cho tới khi janitor dọn phòng rỗng.
                  //
                  // Không tự đóng phòng ở đây: đường ra là sự kiện
                  // `session.ended` từ server (xem api.endSession). Thất bại
                  // thì phải Ở LẠI và nói ra.
                  api.endSession(ctx.sessionID, actor).catch(() => {
                    setEndError(t('ui.host.end_failed'))
                  })
                }}
              >
                <PhoneX size={16} weight="light" />
                {t(endConfirm ? 'ui.host.end_confirm' : 'ui.host.end')}
              </button>
            </div>
            {endConfirm && <p className="meta warn">{t('ui.host.end_hint')}</p>}
            {endError && <p className="msg err">{endError}</p>}

            {/* Link tải bản ghi vừa xong. Chỉ hiện ở chế độ server, và chỉ sau
                khi media plane XÁC NHẬN file đã lên kho — không phải lúc host
                bấm Dừng. Giữa hai mốc đó egress còn phải hoàn tất và tải file,
                và một link hiện sớm là một link trỏ tới chỗ trống. */}
            {shareLink && (
              <div className="share-rec">
                <h2>{t('ui.recshare.host_heading')}</h2>
                {shareLink.passcode_set ? (
                  <>
                    {/* Nói rõ mật khẩu là gì. Không nói thì host gửi link đi rồi
                        nhận lại hai mươi tin nhắn hỏi "mật khẩu nào?". */}
                    <p className="meta">{t('ui.recshare.host_hint')}</p>
                    <input
                      readOnly
                      value={shareLink.url}
                      onFocus={(e) => e.currentTarget.select()}
                      aria-label={t('ui.recshare.host_heading')}
                    />
                    <button
                      className="sec sm"
                      onClick={async () => {
                        try {
                          // navigator.clipboard chỉ tồn tại ở ngữ cảnh bảo mật,
                          // và Z-Meet có thể chạy sau một proxy HTTP nội bộ.
                          // Im lặng không chép gì là cách host dán một chuỗi
                          // rỗng vào chat mà không biết.
                          if (!navigator.clipboard) throw new Error('no clipboard')
                          await navigator.clipboard.writeText(shareLink.url)
                          setShareNote(t('ui.meetinvite.copied'))
                        } catch {
                          setShareNote(t('ui.meetinvite.copy_failed'))
                        }
                      }}
                    >
                      <Copy size={15} weight="light" />
                      {t('ui.meetinvite.copy')}
                    </button>
                  </>
                ) : (
                  // Cuộc họp không có passcode thì link kia từ chối TẤT CẢ mọi
                  // người — mật khẩu của nó chính là passcode. Nói ngay, thay vì
                  // để host gửi đi một link không ai mở được.
                  <div className="msg err">{t('ui.recshare.host_no_passcode')}</div>
                )}
                {shareNote && <p className="meta">{shareNote}</p>}
              </div>
            )}

            <h2>{t('ui.host.waiting_heading')}</h2>
            <ul className="list">
              {waiting.length === 0 ? (
                <li className="empty">{t('ui.host.nobody_waiting')}</li>
              ) : (
                waiting.map((w) => (
                  <li key={w.participant_id}>
                    <div>{w.display_name}</div>
                    <div className="meta">
                      {t('ui.host.waiting_since', {
                        time: new Date(w.requested_at).toLocaleTimeString(),
                      })}
                    </div>
                    {/* Người này đã bị mời ra và đang xin vào lại. Nói ra TRƯỚC
                        khi host bấm Duyệt: cùng một cái tên gõ cửa lần thứ hai
                        trông hệt lần thứ nhất, mà lần thứ hai mới là lần host
                        vừa tự tay đuổi họ. */}
                    {w.was_removed && (
                      <div className="meta warn">
                        <Prohibit size={13} weight="light" />
                        {t('ui.host.was_removed')}
                      </div>
                    )}
                    <div className="acts">
                      <button
                        className="sm"
                        onClick={async () => {
                          await api.admit(ctx.sessionID, w.participant_id, actor)
                          onRefreshWaiting()
                        }}
                      >
                        <Check size={15} weight="light" />
                        {t('ui.host.admit')}
                      </button>
                      {/* Từ chối, KHÔNG cấm: host thường chỉ đang dọn hàng đợi.
                          Trước đây nút này gọi kick, nên mỗi lần dọn hàng đợi là
                          một lệnh cấm ghi cho người chỉ vào nhầm phòng. */}
                      <button
                        className="sec sm"
                        onClick={async () => {
                          await api.deny(ctx.sessionID, w.participant_id, actor)
                          onRefreshWaiting()
                        }}
                      >
                        <X size={15} weight="light" />
                        {t('ui.host.reject')}
                      </button>
                      <button
                        className="danger sm"
                        onClick={async () => {
                          await api.kick(ctx.sessionID, w.participant_id, actor, '', true)
                          onRefreshWaiting()
                        }}
                      >
                        <Prohibit size={15} weight="light" />
                        {t('ui.host.ban')}
                      </button>
                    </div>
                  </li>
                ))
              )}
            </ul>
          </aside>
        )}
      </div>

      {bgError && <div className="banner">{bgError}</div>}
      {screenError && <div className="banner">{screenError}</div>}
      {recError && <div className="banner">{recError}</div>}

      {bgPanel && (
        <div className="bg-panel">
          <p className="hint">{t('ui.room.background_local')}</p>
          <button
            className={'sec' + (bg.kind === 'none' ? ' on' : '')}
            onClick={() => void applyBg({ kind: 'none' })}
          >
            {t('ui.room.background_none')}
          </button>
          <button
            className={'sec' + (bg.kind === 'blur' ? ' on' : '')}
            onClick={() => void applyBg({ kind: 'blur' })}
          >
            {t('ui.room.background_blur')}
          </button>

          {images.map((img) => (
            <div key={img.id} className="bg-thumb">
              <button
                className={bg.kind === 'image' && bg.url === img.dataURL ? 'on' : ''}
                style={{ backgroundImage: `url(${img.dataURL})` }}
                aria-label={t('ui.room.background_image')}
                onClick={() => void applyBg({ kind: 'image', url: img.dataURL })}
              />
              <button
                className="sec sm remove"
                aria-label={t('ui.room.background_remove')}
                onClick={() => {
                  setImages(removeImage(img.id))
                  // Đang dùng đúng ảnh vừa xoá thì quay về không nền, nếu không
                  // nền vẫn hiện mà không còn ô nào sáng để tắt nó.
                  if (bg.kind === 'image' && bg.url === img.dataURL) void applyBg({ kind: 'none' })
                }}
              >
                <X size={11} weight="light" />
              </button>
            </div>
          ))}

          {images.length < MAX_IMAGES && (
            <button className="sec" onClick={() => fileRef.current?.click()}>
              <UploadSimple size={16} weight="light" />
              {t('ui.room.background_upload')}
            </button>
          )}

          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            hidden
            onChange={async (e) => {
              const file = e.target.files?.[0]
              // Xoá value ngay: không có nó thì chọn LẠI cùng một file không
              // kích hoạt onChange, và người dùng tưởng nút hỏng.
              e.target.value = ''
              if (!file) return
              try {
                const list = await addImage(file)
                setImages(list)
                await applyBg({ kind: 'image', url: list[0]!.dataURL })
              } catch {
                setBgError(t('ui.room.background_bad_image'))
              }
            }}
          />
        </div>
      )}

      {pickReaction && (
        <div className="reaction-bar">
          {CATALOG.map((e) => (
            <button
              key={e.name}
              className="sec"
              aria-label={e.name}
              onClick={() => {
                void data.sendReaction(e.name)
                setPickReaction(false)
              }}
            >
              <Emoji name={e.name} size={30} />
            </button>
          ))}
        </div>
      )}

      <div className="controls">
        <button
          className={'icon sec' + (room.micOn ? '' : ' off')}
          aria-label={t(room.micOn ? 'ui.room.mute' : 'ui.room.unmute')}
          onClick={() => void room.setMic(!room.micOn)}
        >
          {room.micOn ? <Microphone size={20} weight="light" /> : <MicrophoneSlash size={20} weight="light" />}
        </button>

        <button
          className={'icon sec' + (room.camOn ? '' : ' off')}
          aria-label={t(room.camOn ? 'ui.room.camera_off' : 'ui.room.camera_on')}
          onClick={() => void room.setCam(!room.camOn)}
        >
          {room.camOn ? <VideoCamera size={20} weight="light" /> : <VideoCameraSlash size={20} weight="light" />}
        </button>

        {room.screenSupported && (
        <button
          className={'icon sec' + (room.screenOn ? ' on' : '')}
          aria-label={t(room.screenOn ? 'ui.room.stop_share' : 'ui.room.share_screen')}
          onClick={async () => {
            setScreenError('')
            try {
              await room.setScreen(!room.screenOn)
            } catch (e) {
              // Người dùng bấm Huỷ đã được nuốt ở tầng dưới; tới đây là lỗi thật.
              setScreenError(t('ui.room.share_failed', { error: (e as Error).message }))
            }
          }}
        >
          <Monitor size={20} weight="light" />
        </button>
        )}

        {/* Ghi hình cục bộ. KHÔNG có hàng rào vai trò: bản ghi nằm lại trên máy
            người bấm và không tiêu tài nguyên nào của cuộc họp, nên chặn nó
            theo vai trò chỉ đuổi người ta sang OBS — nơi cả phòng không nhìn
            thấy gì cả. Cái đáng làm là để việc ghi hình diễn ra CÔNG KHAI. */}
        {/* ⚠️ `!serverMode` là điều kiện KHÔNG bỏ được, không phải một tinh
            chỉnh giao diện. Ở chế độ server, ghi hình là việc của host và bản
            ghi thuộc về workspace kèm chính sách lưu trữ; để nút này cạnh nó
            nghĩa là mọi người vẫn tự ghi được một bản riêng nằm ngoài mọi chính
            sách đó — tức là chế độ server không còn nghĩa gì.

            Cưỡng chế được tới đâu thì nói tới đó: ẩn nút không ngăn được người
            mở OBS. Xem roomdata.ts về giới hạn của cờ ghi hình. */}
        {recSupported() && !serverMode && (
          <button
            className={'icon sec' + (rec.recording ? ' rec' : '')}
            aria-label={t(rec.recording ? 'ui.localrec.stop' : 'ui.localrec.start')}
            aria-pressed={rec.recording}
            onClick={async () => {
              setRecError('')
              if (rec.recording) {
                rec.stop()
                return
              }
              try {
                await rec.start()
              } catch (e) {
                // Người dùng bấm Huỷ đã được nuốt ở tầng dưới; tới đây là lỗi
                // thật. Im lặng ở đây nghĩa là họ tưởng đang ghi cả buổi họp.
                setRecError(t('ui.localrec.failed', { error: (e as Error).message }))
              }
            }}
          >
            {rec.recording ? <StopCircle size={20} weight="light" /> : <Record size={20} weight="light" />}
          </button>
        )}

        {/* Nút nền chỉ hiện khi trình duyệt kham nổi. Hiện rồi báo lỗi khi bấm
            là bắt người dùng phát hiện giới hạn nền tảng thay cho ta. */}
        {bgSupported() && (
          <button
            className={'icon sec' + (bg.kind === 'none' && !bgPanel ? '' : ' on')}
            aria-label={t('ui.room.background')}
            onClick={() => setBgPanel((v) => !v)}
          >
            <ImageSquare size={20} weight="light" />
          </button>
        )}

        <button
          className={'icon sec' + (pickReaction ? ' on' : '')}
          aria-label={t('ui.room.react')}
          onClick={() => setPickReaction((v) => !v)}
        >
          <SmileyWink size={20} weight="light" />
        </button>

        <button
          className={'icon sec' + (panel === 'chat' ? ' on' : '')}
          aria-label={t('ui.chat.heading')}
          onClick={() => {
            setPanel(panel === 'chat' ? 'none' : 'chat')
            data.clearUnread()
          }}
        >
          <ChatCircle size={20} weight="light" />
          {data.unread > 0 && panel !== 'chat' && <span className="badge">{data.unread}</span>}
        </button>

        {canInvite && (
          <button
            className={'icon sec' + (panel === 'invite' ? ' on' : '')}
            aria-label={t('ui.meetinvite.title')}
            onClick={() => setPanel(panel === 'invite' ? 'none' : 'invite')}
          >
            <UserPlus size={20} weight="light" />
          </button>
        )}

        {isHost && (
          <button
            className={'icon sec' + (panel === 'people' ? ' on' : '')}
            aria-label={t('ui.host.heading')}
            onClick={() => setPanel(panel === 'people' ? 'none' : 'people')}
          >
            <UserMinus size={20} weight="light" />
            {waiting.length > 0 && panel !== 'people' && (
              <span className="badge">{waiting.length}</span>
            )}
          </button>
        )}

        <button className="icon danger" aria-label={t('ui.join.leave')} onClick={onLeave}>
          <PhoneX size={20} weight="light" />
        </button>
      </div>
    </>
  )
}

/**
 * InvitePanel — thông tin để mời một người ngoài vào phòng.
 *
 * Hai trạng thái, và sự khác nhau giữa chúng là toàn bộ điểm của màn hình này:
 *
 *	chưa mở  cuộc họp chỉ nhận người được hệ thống ngoài phát vé. Panel hiện
 *	         link nhưng KHÔNG hiện passcode, và một cái nút nói rõ bấm vào là
 *	         mở ra cho ai có link + passcode.
 *	đã mở    hiện đủ, kèm nút chép.
 *
 * Việc mở là một lời gọi RIÊNG với `open: true`. Gộp nó vào lần đọc đầu tiên
 * sẽ nới chính sách của cuộc họp chỉ vì ai đó tò mò bấm vào một cái nút.
 */
function InvitePanel({
  ctx,
  onClose,
}: {
  ctx: RoomCtx
  onClose: () => void
}) {
  const { t } = useI18n()
  const [info, setInfo] = useState<api.MeetingInvite | null>(null)
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState('')

  const load = async (open: boolean) => {
    setBusy(true)
    setNote('')
    try {
      const r = await api.meetingInvite(ctx.sessionID, ctx.participantID, ctx.principal, open)
      if (r.ok) setInfo(r.data)
      else setNote(r.data?.message ?? t('ui.meetinvite.locked'))
    } finally {
      setBusy(false)
    }
  }

  useEffect(() => {
    void load(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.sessionID, ctx.participantID])

  // Đoạn văn bản đem đi dán — dựng từ khoá dịch, không nối chuỗi tại chỗ: nó là
  // thứ người dùng gửi cho người khác đọc, nên nó phải ở đúng thứ tiếng họ đang
  // nhìn giao diện.
  const shareText = () => {
    const vars = {
      title: info?.title ?? '',
      url: info?.join_url ?? '',
      code: info?.join_code ?? '',
      passcode: info?.passcode ?? '',
    }
    return t(info?.passcode ? 'ui.meetinvite.share_body' : 'ui.meetinvite.share_body_nopass', vars)
  }

  const copy = async () => {
    const text = shareText()
    try {
      // navigator.clipboard chỉ tồn tại ở ngữ cảnh bảo mật, và Z-Meet có thể
      // đang chạy sau một proxy HTTP trong mạng nội bộ. Không có nó thì nói
      // thẳng — im lặng không chép gì là cách người dùng gửi đi một tin nhắn
      // rỗng mà không biết.
      if (!navigator.clipboard) throw new Error('no clipboard')
      await navigator.clipboard.writeText(text)
      setNote(t('ui.meetinvite.copied'))
    } catch {
      setNote(t('ui.meetinvite.copy_failed'))
    }
  }

  return (
    <aside className="sidebar">
      <div className="row">
        <h2 style={{ flex: 1 }}>{t('ui.meetinvite.title')}</h2>
        <button className="sec sm" onClick={onClose} aria-label={t('ui.chat.close')}>
          <X size={14} weight="light" />
        </button>
      </div>

      {info?.join_url && (
        <>
          <label>{t('ui.meetinvite.link')}</label>
          <input readOnly value={info.join_url} onFocus={(e) => e.currentTarget.select()} />
          <label>{t('ui.meetinvite.code')}</label>
          <input readOnly value={info.join_code ?? ''} onFocus={(e) => e.currentTarget.select()} />
        </>
      )}

      {info?.passcode && (
        <>
          <label>{t('ui.meetinvite.passcode')}</label>
          <input readOnly value={info.passcode} onFocus={(e) => e.currentTarget.select()} />
        </>
      )}

      {/* Chưa mở: nói rõ bấm vào thì điều gì đổi. "Cho phép vào bằng passcode"
          là một câu về hậu quả, không phải một nhãn về cơ chế. */}
      {info && !info.shareable && (
        <>
          <p className="sub">
            {t(
              info.open_effect === 'add_passcode'
                ? 'ui.meetinvite.open_hint_passcode'
                : 'ui.meetinvite.open_hint',
            )}
          </p>
          <button className="wide" disabled={busy} onClick={() => void load(true)}>
            <LockKey size={16} weight="light" />
            {t('ui.meetinvite.open_action')}
          </button>
        </>
      )}

      {info?.shareable && (
        <button className="wide" disabled={busy} onClick={() => void copy()}>
          <Copy size={16} weight="light" />
          {t('ui.meetinvite.copy')}
        </button>
      )}

      {/* Nói trước điều người kia sẽ gặp. `blocked` là hộp ĐỎ chứ không phải
          một dòng ghi chú: gửi thông tin đi rồi mới biết cửa đóng là cách người
          dùng kết luận rằng tính năng này hỏng. */}
      {info?.shareable && info.entry === 'lobby' && (
        <p className="sub">{t('ui.meetinvite.entry_lobby')}</p>
      )}
      {info?.shareable && info.entry === 'blocked' && (
        <div className="msg err">{t('ui.meetinvite.entry_blocked')}</div>
      )}
      {note && <div className="msg wait">{note}</div>}
    </aside>
  )
}

type ModerateAction =
  | 'mute-audio'
  | 'mute-video'
  | 'stop-screen'
  | 'ask-audio'
  | 'ask-video'
  | 'block'
  | 'allow'
  // Hai mức đuổi, cố ý không gộp. 'kick' cho vào lại (qua phòng chờ), 'ban'
  // thì không — xem api.kick.
  | 'kick'
  | 'ban'

function VideoTile({
  tile,
  canModerate,
  recording,
  reactions,
  onModerate,
}: {
  tile: Tile
  canModerate: boolean
  /** Người này đang ghi hình về máy họ — xem recorder.ts. */
  recording: boolean
  reactions: string[]
  onModerate: (a: ModerateAction) => void
}) {
  const { t } = useI18n()
  const videoRef = useRef<HTMLVideoElement>(null)
  const audioRef = useRef<HTMLAudioElement>(null)
  const [menu, setMenu] = useState(false)

  useEffect(() => {
    const el = videoRef.current
    if (el) el.srcObject = tile.video ? new MediaStream([tile.video]) : null
  }, [tile.video])

  useEffect(() => {
    const el = audioRef.current
    if (el) el.srcObject = tile.audio ? new MediaStream([tile.audio]) : null
  }, [tile.audio])

  return (
    <div className={'tile' + (tile.speaking ? ' speaking' : '') + (tile.isScreen ? ' screen' : '')}>
      {tile.video ? (
        // muted trên ô của CHÍNH MÌNH là bắt buộc: không có nó thì micro của
        // mình phát ra loa của mình và tạo hú.
        <video ref={videoRef} autoPlay playsInline muted={tile.isLocal} />
      ) : (
        <div className="placeholder">{tile.name.slice(0, 1).toUpperCase()}</div>
      )}
      {!tile.isLocal && <audio ref={audioRef} autoPlay />}

      {reactions.length > 0 && (
        <div className="reactions">
          {reactions.map((name, i) => (
            <Emoji key={i} name={name} size={44} />
          ))}
        </div>
      )}

      <div className="name">
        {/* Dấu ghi hình đứng TRƯỚC tên: nó là thứ người nhìn ô này cần thấy
            đầu tiên, và một tên dài đẩy nó ra khỏi mép ô nếu nó đứng sau. */}
        {recording && (
          <Record size={13} weight="fill" className="rec" aria-label={t('ui.localrec.tile')} />
        )}
        {tile.isScreen ? (
          <Monitor size={13} weight="light" />
        ) : (
          tile.muted && <MicrophoneSlash size={13} weight="light" />
        )}
        {tile.isScreen ? t('ui.room.sharing_by', { name: tile.name }) : tile.name}
        {/* Dấu vai trò, đọc từ metadata của LiveKit — tức là từ vai trò THẬT mà
            server đặt lúc vào phòng (xem meeting.ParticipantMeta), không phải
            từ một chuỗi ai đó nhét vào tên hiển thị.

            Khác biệt ấy quan trọng: tên hiển thị đến từ hệ thống ngoài và người
            dùng tự đặt được, nên "Nguyễn Văn A (Host)" là thứ bất kỳ ai cũng
            gõ ra được. Dấu này thì không — nó không hiện ra nếu server không
            nói người đó là host.

            Chỉ host và cohost có dấu. Presenter và attendee thì không: một dấu
            trên mọi ô là một dấu không nói gì. */}
        {!tile.isScreen && ROLE_BADGES[tile.role] && (
          <span className="role">{t(ROLE_BADGES[tile.role]!)}</span>
        )}
      </div>

      {canModerate && tile.pid && tile.isScreen && (
        // Ô màn hình có ĐÚNG MỘT hành động có nghĩa. Nhét cả menu điều hành
        // vào đây là mời host bấm "tắt micro" lên một cái màn hình.
        <div className="tile-menu">
          <button className="danger sm" onClick={() => onModerate('stop-screen')}>
            <Prohibit size={14} weight="light" />
            {t('ui.host.stop_screen')}
          </button>
        </div>
      )}

      {canModerate && tile.pid && !tile.isScreen && (
        <div className="tile-menu">
          <button className="sec sm" onClick={() => setMenu((v) => !v)} aria-label={t('ui.host.moderate')}>
            ⋯
          </button>
          {menu && (
            <div
              className="menu"
              onMouseLeave={() => setMenu(false)}
              onClick={() => setMenu(false)}
            >
              <button className="sec sm" onClick={() => onModerate('mute-audio')}>
                <MicrophoneSlash size={14} weight="light" />
                {t('ui.host.mute_audio')}
              </button>
              <button className="sec sm" onClick={() => onModerate('mute-video')}>
                <VideoCameraSlash size={14} weight="light" />
                {t('ui.host.mute_video')}
              </button>
              <button className="sec sm" onClick={() => onModerate('ask-audio')}>
                <Microphone size={14} weight="light" />
                {t('ui.host.ask_unmute_audio')}
              </button>
              <button className="sec sm" onClick={() => onModerate('ask-video')}>
                <VideoCamera size={14} weight="light" />
                {t('ui.host.ask_unmute_video')}
              </button>
              <button className="sec sm" onClick={() => onModerate('block')}>
                <Prohibit size={14} weight="light" />
                {t('ui.host.block_publish')}
              </button>
              <button className="sec sm" onClick={() => onModerate('allow')}>
                <Check size={14} weight="light" />
                {t('ui.host.allow_publish')}
              </button>
              {/* Hai mức đuổi, hai cái nút, hai câu chữ nói rõ hậu quả khác
                  nhau. Gộp thành một nút "đuổi" rồi để host đoán xem người kia
                  có quay lại được không là đúng thứ Zoom làm với "tắt tiếng" và
                  "cấm bật tiếng" — xem internal/meeting/moderate.go. */}
              <button className="danger sm" onClick={() => onModerate('kick')}>
                <UserMinus size={14} weight="light" />
                {t('ui.host.remove')}
              </button>
              <button className="danger sm" onClick={() => onModerate('ban')}>
                <Prohibit size={14} weight="light" />
                {t('ui.host.ban')}
              </button>
              <p className="hint">{t('ui.host.remove_hint')}</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
